import { autobind } from "macguffin"
import { createTween, createRotationTween, chainTweens } from "utils/TweenHelper"
import { Easing } from "@tweenjs/tween.js"
import Timings from "game/Timings"
import * as FirstStage from "./firstStage"
import * as SecondStage from "./secondStage"

const { TAKEOFF_DELAY, TAKEOFF_DURATION } = Timings

export default class LaunchProfiles {
    constructor(rocket) {
        this.rocket = rocket
    }

    applyProperties(properties) {
        const { firstStage, secondStage, thirdStage } = this.rocket
        const { props1, props2, props3 } = properties

        props1 && firstStage.setPosition(props1)
        props2 && secondStage.setPosition(props2)
        props3 && thirdStage.setPosition(props3)
    }

    resumeLaunch() {
        const { launchExhaust, mainThruster } = this.rocket

        mainThruster.start()
    }

    //resuming into the freespins
    resumeFirstStage() {
        const { rocket } = this
        const { firstStage, secondStage } = rocket

        const firstSeparation = FirstStage.getSeparation(rocket)
        this.applyProperties(firstSeparation)

        const firstDrift = FirstStage.getDrift(rocket)
        this.applyProperties(firstDrift)

        rocket.removeChild(firstStage)
        secondStage.startThruster()
    }

    resumeSecondStage() {
        const { rocket } = this
        const { secondStage, thirdStage } = rocket

        this.resumeFirstStage()

        const secondSeparation = SecondStage.getSeparation(rocket)
        this.applyProperties(secondSeparation)

        rocket.removeChild(secondStage)
        thirdStage.startThruster()
    }

    stopTweens() {
        this.exhaustTween && this.exhaustTween.stop()
        this.thirdStageTween && this.thirdStageTween.stop()
        this.secondStageTween && this.secondStageTween.stop()
        this.firstStageTween && this.firstStageTween.stop()
    }

    //ROCKET FLIGHT ANIMATIONS

    launch() {
        const { launchCoolant, launchExhaust, mainThruster, rocketHeight } = this.rocket

        launchCoolant.stop()
        launchExhaust.start()
        mainThruster.start()

        const launchProperties = {
            delay: TAKEOFF_DELAY,
            y: rocketHeight,
            duration: TAKEOFF_DURATION * 0.85,
            easing: Easing.Exponential.In,
            onComplete: () => { launchExhaust.stop() }
        }

        this.exhaustTween = createTween(launchExhaust, launchProperties, true)
    }

    leaveAtmosphere() {
        const { firstStage, mainThruster } = this.rocket

        mainThruster.stop()
        firstStage.startThruster()
    }

    ejectFirstStage(instant) {
        if (instant) {
            this.resumeFirstStage()
            return
        }

        const { rocket } = this
        const { firstStage, secondStage, thirdStage } = rocket
        const { props3, props2, props1, sharedProperties } = FirstStage.getSeparation(rocket)
        const onComplete = this.startFirstDrift

        this.thirdStageTween = createTween(thirdStage, {...props3, ...sharedProperties}, true)
        this.secondStageTween =  createTween(secondStage, {...props2, ...sharedProperties}, true)
        this.firstStageTween = createTween(firstStage, {...props1, ...sharedProperties, onComplete}, true)

        firstStage.stopThruster()
        secondStage.dettachLowerStage()
    }

    startFirstDrift = () => {
        const { rocket } = this
        const { firstStage, secondStage, thirdStage, heights, offsets, resolution } = rocket
        const { props1, props2, props3, sharedProperties } = FirstStage.getDrift(rocket)
        const onComplete = this.loopFirstDrift

        this.thirdStageTween = createTween(thirdStage, {...props3, ...sharedProperties}, true)
        this.secondStageTween = createTween(secondStage, {...props2, ...sharedProperties}, true)
        this.firstStageTween = createTween(firstStage, {...props1, ...sharedProperties, onComplete}, true)

        secondStage.startThruster()
    }

    loopFirstDrift = () => {
        const { rocket } = this
        const { props, firstStage } = rocket
        const { stageHeight } = props

        if (((firstStage.y - firstStage.height) > stageHeight) && ((firstStage.x - firstStage.width) < 0)) {
            rocket.removeChild(firstStage)
            return
        }

        const tweenProperties = FirstStage.getDriftLoop(rocket)
        const onComplete = this.loopFirstDrift

        this.firstStageTween = createTween(firstStage, {...tweenProperties, onComplete}, true)
    }

    ejectSecondStage(instant) {
        if (instant) {
            this.resumeSecondStage()
            return
        }

        const { rocket } = this
        const { firstStage, secondStage, thirdStage } = rocket
        const { props2, props3, sharedProperties } = SecondStage.getSeparation(rocket)
        const onComplete = this.startSecondDrift

        this.thirdStageTween = createTween(thirdStage, {...props3, ...sharedProperties}, true)
        this.secondStageTween = createTween(secondStage, {...props2, ...sharedProperties, onComplete}, true)

        secondStage.stopThruster()
        thirdStage.dettachLowerStage()
    }

    startSecondDrift = () => {
        const { rocket } = this
        const { secondStage, thirdStage } = rocket
        const tweenProperties = SecondStage.getDrift(rocket)
        const onComplete = this.loopSecondDrift

        this.secondStageTween = createTween(secondStage, {...tweenProperties, onComplete}, true)
        thirdStage.startThruster()
    }

    loopSecondDrift = () => {
        const { rocket } = this
        const { props, secondStage } = rocket
        const { stageHeight } = props

        if (((secondStage.y - secondStage.height) > stageHeight) && ((secondStage.x - secondStage.width) < 0)) {
            rocket.removeChild(secondStage)
            return
        }

        const tweenProperties = SecondStage.getDriftLoop(rocket)
        const onComplete = this.loopSecondDrift

        this.secondStageTween = createTween(secondStage, {...tweenProperties, onComplete}, true)
    }
}
